import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from './supabase';

/* =============================================================================
 * Calling an Edge Function without losing the reason it failed
 * -----------------------------------------------------------------------------
 * supabase-js throws away the response body on any non-2xx:
 *
 *     if (!response.ok) { throw new FunctionsHttpError(response) }
 *
 * The body is never read, so `data` comes back null and the only thing the
 * caller sees is the literal string "Edge Function returned a non-2xx status
 * code". Every function in this project answers a refusal with a written
 * explanation — "That file is not available to you", "Set a sign-in domain
 * first", "That is an SMTP key, not an API key" — and all of it was being
 * discarded before anyone could read it.
 *
 * FunctionsHttpError carries the original Response on `context`, so the body is
 * still there to be recovered. This does that, once, for every caller.
 * ========================================================================== */

export interface InvokeOptions {
  /**
   * JSON object, or FormData for an upload.
   *
   * Typed as supabase-js types it. FormData is what makes this wider than a
   * plain record — an upload posts multipart, and the client must be left to
   * set the boundary itself.
   */
  body?: FormData | Record<string, unknown>;
}

/**
 * Invokes an Edge Function and throws an Error carrying the server's own words.
 *
 * Throws rather than returning `{ data, error }`: every caller here is inside a
 * react-query mutation, which already turns a thrown error into state on the
 * screen. Returning a tuple only invites the next person to forget to check it.
 */
export async function invokeFunction<T>(
  name: string,
  options: InvokeOptions = {},
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, {
    ...(options.body !== undefined ? { body: options.body } : {}),
  });

  if (error) {
    throw await asError(name, error);
  }

  // A function that answers 200 with `{ error }` — send-email does this, so a
  // refused send still records itself in the delivery log.
  const payload = data as (T & { error?: string }) | null;
  if (payload && typeof payload === 'object' && 'error' in payload && payload.error) {
    throw new Error(String(payload.error));
  }

  return payload as T;
}

/**
 * Recovers the message the function actually sent.
 *
 * Falls back through progressively vaguer options rather than ever surfacing
 * "non-2xx status code", which tells the person in front of the screen nothing
 * they can act on.
 */
/**
 * The thrown error, carrying anything else the function chose to send.
 *
 * `explain` reduces a refusal to one sentence, which is what almost every
 * caller wants. Some responses carry a second field alongside it — send-email
 * returns the mail provider's own words to a platform operator and to nobody
 * else — and reducing the body to a string threw that away before the screen
 * that asked for it could read it.
 *
 * Attached to the Error rather than returned separately so that every existing
 * caller is unaffected: `.message` is exactly what it was.
 */
async function asError(name: string, error: unknown): Promise<Error> {
  const thrown = new Error(await explain(name, error)) as Error & { detail?: string };

  if (error instanceof FunctionsHttpError) {
    try {
      const body = await (error.context as Response | undefined)?.clone().json();
      if (body?.detail) thrown.detail = String(body.detail);
    } catch {
      /* Not JSON, or already read. The sentence above stands on its own. */
    }
  }

  return thrown;
}

async function explain(name: string, error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    const response = error.context as Response | undefined;

    try {
      // Cloned because the caller may want to read it too, and a Response body
      // can only be consumed once.
      const body = await response?.clone().json();
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    } catch {
      // Not JSON. Try it as text before giving up on it.
      try {
        const text = await response?.clone().text();
        if (text?.trim()) return text.slice(0, 300);
      } catch {
        /* body already consumed or unreadable */
      }
    }

    if (response?.status === 404) {
      /* i18n-exempt: this one is for whoever deploys, not for whoever works
         here. It names a shell command, and a shell command has no Arabic —
         translating the sentence around it would only make the instruction
         harder to act on. It reaches a screen once, on a machine that is
         missing a function nobody has pushed yet. */
      return `The ${name} function is not deployed. Run: supabase functions deploy ${name}`;
    }
    if (response?.status === 401) {
      return 'Your session has expired. Sign in again.';
    }
    // i18n-exempt: a function name and an HTTP status, for the same reader.
    return `${name} failed with status ${response?.status ?? 'unknown'}.`;
  }

  if (error instanceof Error) {
    // A network-level failure. On a site with no signal this is the common one,
    // and naming it stops it being read as a bug in the app.
    if (/failed to (send|fetch)/i.test(error.message)) {
      return 'Could not reach the server. Check the connection and try again.';
    }
    return error.message;
  }

  return String(error);
}
